import type { SucessErrorMsg } from 'nuxt-base/composables/useNotify'

export type InputCartItem = { productId: string; qty: number }
type CartSyncOperation = 'add' | 'set'

export type CartItem = {
  productId: string
  qty: number
  priceTotal: number
  priceTotalForHumans: string
  cart: gen.BuyerCartFragment['items'][number] | None
  uiPrice: gen.ProductListingBoxUiPriceFragment | None
}

function cartFormProduct(i: InputCartItem, uiPrice: None | gen.ProductListingBoxUiPriceFragment, cart: CartItem['cart']): CartItem {
  const priceTotal = cart?.priceTotal?.number || helpers.priceMx(uiPrice?.price?.number || 0, i.qty)
  return {
    qty: i.qty,
    productId: i.productId,
    cart,
    uiPrice,
    priceTotal,
    priceTotalForHumans: cart?.priceTotal?.priceForHumans || helpers.priceFormat(priceTotal),
  }
}

export function createCartData() {
  const cartInited = useState('auth:cart:init', () => shallowRef({ on: false }))
  const original = useState<gen.BuyerCartFragment | None>('auth:cart2:original', () => shallowRef(null))
  const input = useState<{ items: CartItem[]; debounce: number; operation: CartSyncOperation }>('auth:cart', () => shallowRef({ items: [], debounce: 600, operation: 'add' }))
  const synced = useState('auth:cart:synced', () => shallowRef(true))
  return { original, cartInited, input, synced, cartFormProduct }
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function useCart() {
  // only manipulates createCartData state; safe to call many times per page
  const { input, original, cartInited, synced } = createCartData()
  const nuxt = useNuxtApp()
  let cbs: ((i: SucessErrorMsg) => void)[] = []
  const cleanup = useCleanup()
  cleanup(() => (cbs = []))

  nuxt.hook('my:cartUpdate', (res) => {
    cbs.forEach((i) => i(res))
    cbs = []
  })

  const init = () => nextTick(() => !cartInited.value.on && (cartInited.value = { on: true }))

  function findOneCartItem(id: string | None): CartItem['cart'] {
    init()
    return helpers.arrFindId(original.value?.items, id, (i) => i.productId)
  }

  function findOneQty(id: string | None): number {
    const qty = helpers.arrFindId(input.value.items, id, (i) => i.productId)?.qty
    return typeof qty === 'number' ? qty : helpers.toPositive(findOneCartItem(id)?.qty, 0)
  }

  function setItems(items: CartItem[], debounce = 600): void {
    init()
    input.value = { items, debounce, operation: 'set' }
  }

  // SYNC. Only writes local state; useCartInit's watcher does the rest.
  function add(inp: InputCartItem | InputCartItem[], debounce = 600): void {
    init()
    const listings = helpers.arr(helpers.arrify(inp), (item) => {
      const prev = helpers.arrFindId(input.value.items, item.productId, (i) => i.productId)
      return cartFormProduct(item, prev?.uiPrice, prev?.cart)
    })
    const copy = [...input.value.items]
    listings.forEach((doc) => {
      const index = copy.findIndex((i) => i.productId === doc.productId)
      if (index === -1 && doc.qty > 0) copy.unshift(doc)
      if (index === -1) return
      copy[index] = doc
    })
    input.value = { items: copy, debounce, operation: 'add' }
  }

  // Force an immediate flush; resolves when that round completes.
  function syncCart(): Promise<SucessErrorMsg> {
    init()
    const promise = useRemotePromise<SucessErrorMsg>({ noDisopse: true })
    cbs.push((i) => promise.resolve(i))
    input.value = { items: input.value.items, debounce: 0, operation: 'add' } // re-arm watcher, zero debounce
    return promise.wait()
  }

  return {
    ui: toRef(() => original.value),
    add,
    items(): CartItem[] {
      init()
      return input.value.items
    },
    findOneCartItem,
    findOneQty,
    isCartSynced: computed(() => synced.value),
    syncCart,
    setItems,
    cartFormProduct,
  }
}
